import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import ImageStudioPicker from "@/components/ImageStudioPicker";
import LookbookGenerator from "@/components/LookbookGenerator";
import ShopVisibility from "@/components/ShopVisibility";
import WaitlistPanel from "@/components/WaitlistPanel";
import DangerZone from "@/components/DangerZone";
import PhotoBoard from "@/components/PhotoBoard";
import InstagramPreview from "@/components/InstagramPreview";
import InstagramPrepModal from "@/components/InstagramPrepModal";
import PoshmarkPrepModal from "@/components/PoshmarkPrepModal";
import SaveIndicator from "@/components/SaveIndicator";
import { useAutoSave } from "@/lib/useAutoSave";
import { SpecsSection, FinancialsSection } from "@/pages/inventory_edit/Sections";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Sparkles, Globe2, Instagram, ShoppingBag, X, BadgeCheck } from "lucide-react";
import { formatMoney, currencySymbol } from "@/lib/currency";
import { useSettings } from "@/context/SettingsContext";
import { weightLabel, kgToInput, inputToKg } from "@/lib/units";
import { compressImage } from "@/lib/imageCompress";

const STAGES = [
  "Sourcing & Acquisition", "Cleaning & Repairing", "Ironing", "Weighing",
  "Photographing", "Studio Editing", "Listing & Marketing", "Sale Completion",
  "Packaging", "Shipping", "Confirmation of Receipt",
];

function FieldInput({ k, label, type = "text", suffix, value, onChange }) {
  // Numeric fields (financials) auto-select their content on focus so the user can type
  // a new value over the default 0 without having to clear it first.
  const isNumeric = type === "number";
  return (
    <div>
      <label className="editorial-label">{label}</label>
      <div className="flex items-center gap-2 border-b border-taupe mt-2">
        <input
          data-testid={`field-${k}`}
          type={type}
          value={value ?? ""}
          onChange={(e) => onChange(k, isNumeric ? Number(e.target.value) : e.target.value)}
          onFocus={isNumeric ? (e) => e.target.select() : undefined}
          className="w-full bg-transparent py-2 focus:outline-none text-charcoal"
        />
        {suffix && <span className="text-xs text-taupe">{suffix}</span>}
      </div>
    </div>
  );
}

function SaleDialog({ itemId, defaultPrice, onClose, onSaved }) {
  const [price, setPrice] = useState(defaultPrice);
  const [channel, setChannel] = useState("storefront");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/inventory/${itemId}/mark-sold`, {
        sold_price: Number(price),
        sold_channel: channel,
        buyer_name: name || null,
        buyer_email: email || null,
        buyer_phone: phone || null,
        sale_notes: notes || null,
      });
      toast.success("sale registered.");
      onSaved();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "could not register sale.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-charcoal/60 backdrop-blur-sm flex items-center justify-center px-4" onClick={onClose} data-testid="sale-dialog">
      <form onSubmit={submit} className="bg-ivory border border-taupe max-w-xl w-full p-10" onClick={(e) => e.stopPropagation()}>
        <span className="editorial-label">register · sale</span>
        <h2 className="font-serif text-3xl lowercase text-charcoal mt-3 mb-6">a piece has found its story.</h2>
        <div className="grid md:grid-cols-2 gap-x-6 gap-y-5">
          <div>
            <label className="editorial-label">final sold price</label>
            <input data-testid="sale-price" type="number" step="0.01" required value={price} onChange={(e) => setPrice(e.target.value)} onFocus={(e) => e.target.select()} className="w-full bg-transparent border-b border-taupe py-2 mt-1 focus:outline-none focus:border-brass text-charcoal" />
          </div>
          <div>
            <label className="editorial-label">channel</label>
            <select data-testid="sale-channel" value={channel} onChange={(e) => setChannel(e.target.value)} className="w-full bg-transparent border-b border-taupe py-2 mt-1 focus:outline-none text-charcoal">
              <option value="storefront">native storefront</option>
              <option value="ebay">ebay</option>
              <option value="instagram">instagram</option>
              <option value="in-person">in-person</option>
              <option value="other">other</option>
            </select>
          </div>
          <div className="md:col-span-2"><label className="editorial-label">buyer name (optional)</label><input data-testid="sale-buyer-name" value={name} onChange={(e) => setName(e.target.value)} className="w-full bg-transparent border-b border-taupe py-2 mt-1 focus:outline-none focus:border-brass text-charcoal" /></div>
          <div><label className="editorial-label">buyer email</label><input data-testid="sale-buyer-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-transparent border-b border-taupe py-2 mt-1 focus:outline-none focus:border-brass text-charcoal" /></div>
          <div><label className="editorial-label">buyer phone</label><input data-testid="sale-buyer-phone" value={phone} onChange={(e) => setPhone(e.target.value)} className="w-full bg-transparent border-b border-taupe py-2 mt-1 focus:outline-none focus:border-brass text-charcoal" /></div>
          <div className="md:col-span-2"><label className="editorial-label">notes (optional)</label><textarea data-testid="sale-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full bg-transparent border-b border-taupe py-2 mt-1 focus:outline-none focus:border-brass text-charcoal" /></div>
        </div>
        <div className="flex gap-3 mt-8">
          <button type="button" onClick={onClose} className="flex-1 border border-taupe text-charcoal py-3 text-xs tracking-[0.25em] uppercase">cancel</button>
          <button type="submit" disabled={saving} data-testid="sale-submit" className="flex-1 bg-brass hover:bg-espresso text-ivory py-3 text-xs tracking-[0.25em] uppercase disabled:opacity-50">{saving ? "saving…" : "register sale"}</button>
        </div>
      </form>
    </div>
  );
}

export default function AdminInventoryEdit() {
  const { id } = useParams();
  const nav = useNavigate();
  const { settings } = useSettings();
  const unitSystem = settings?.unit_system || "metric";
  const [item, setItem] = useState(null);
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });
  const [enhancingPhotos, setEnhancingPhotos] = useState({}); // {[path]: true} — parallel state
  const [rotatingPhoto, setRotatingPhoto] = useState(null);
  const [studio, setStudio] = useState({ mode: "color", background_color: "#F6F2EB", color_name: "Warm Ivory", custom_background_path: null });
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [saleDialog, setSaleDialog] = useState(false);
  const [igPrepOpen, setIgPrepOpen] = useState(false);
  const [poshPrepOpen, setPoshPrepOpen] = useState(false);
  // Preview QUEUE — enhance is now parallelisable so multiple candidates can arrive
  // for review. The modal always shows the head; accept/discard pops it.
  const [previewQueue, setPreviewQueue] = useState([]); // Array<{source, candidate, mode}>
  const preview = previewQueue[0] || null;

  const load = useCallback(() => api.get(`/inventory/${id}`).then((r) => setItem(r.data)), [id]);
  const loadCategories = useCallback(async () => {
    try {
      const { data } = await api.get("/categories", { params: { include_inactive: true } });
      setCategories(data || []);
    } catch (e) {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn("categories load failed", e);
      }
    }
  }, []);
  useEffect(() => { load(); loadCategories(); }, [load, loadCategories]);

  const update = (k, v) => setItem((p) => ({ ...p, [k]: v }));

  const computedPrice = useMemo(() => {
    if (!item) return 0;
    const f = item.platform_fee_percentage || 0.15;
    return ((Number(item.purchase_cost || 0) + Number(item.shipping_cost || 0) + Number(item.profit_margin || 0)) / Math.max(1 - f, 0.01)).toFixed(2);
  }, [item]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...item };
      delete payload.sale_price;
      const { data } = await api.put(`/inventory/${id}`, payload);
      setItem(data);
      autoSave.markBaseline(data);
      toast.success("piece saved.");
    } catch (e) {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.error("inventory save failed", e);
      }
      toast.error("save failed.");
    } finally {
      setSaving(false);
    }
  };

  // Auto-save the form-field side of the item. Photo + channel + reorder + enhance flows
  // already persist per-action via dedicated handlers, so we limit the auto-save payload to
  // the spec/financials fields to avoid stomping on those server-acknowledged values.
  const autoSavableFields = useMemo(() => {
    if (!item) return null;
    const {
      title, description, sku, brand, collection, fabric, color, type, style, storage_location,
      category, measurements, measurements_map, weight,
      size, size_fit, size_system, size_value,
      purchase_cost, shipping_cost, profit_margin,
      shipping_weight_lb, shipping_length_in, shipping_width_in, shipping_height_in,
    } = item;
    return {
      title, description, sku, brand, collection, fabric, color, type, style, storage_location,
      category, measurements, measurements_map, weight,
      size, size_fit, size_system, size_value,
      purchase_cost, shipping_cost, profit_margin,
      shipping_weight_lb, shipping_length_in, shipping_width_in, shipping_height_in,
    };
  }, [item]);

  const autoSave = useAutoSave({
    value: autoSavableFields,
    save: async (snap) => {
      if (!snap) return snap;
      const { data } = await api.put(`/inventory/${id}`, snap);
      // Merge the server response back into the page so derived values (e.g. sale_price)
      // stay in sync — BUT strip photos + photo_channels first. If a photo reorder /
      // channel toggle / photo remove / upload PUT was in-flight when this auto-save
      // returns, the auto-save's server snapshot carries the pre-reorder photo order
      // and would visibly snap the tiles back. Photos are owned exclusively by the
      // photo handlers below (uploadPhoto, removePhoto, reorderPhotos, toggleChannel).
      setItem((prev) => {
        const merged = { ...(prev || {}), ...data };
        if (prev) {
          merged.photos = prev.photos;
          merged.photo_channels = prev.photo_channels;
        }
        return merged;
      });
      return snap;
    },
    debounceMs: 1500,
  });

  const uploadPhoto = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    setUploadProgress({ done: 0, total: files.length });
    try {
      // Cap concurrent uploads to 3. Firing 6-12 simultaneous multipart POSTs
      // exceeds the browser's per-host connection limit and stampedes the backend's
      // threadpool — measured wall-clock is faster with a small pool because each
      // upload gets a fair share of bandwidth instead of all of them starving.
      const CONCURRENCY = 3;
      let done = 0;
      const uploadOne = async (file) => {
        // Compress client-side (2048px long-edge · JPEG 82%) so a 3-4 MB iPhone
        // camera-roll photo becomes ~450KB — matches the mobile PWA behaviour and
        // dramatically speeds up slow-Wi-Fi booth uploads.
        const compressed = await compressImage(file);
        const fd = new FormData();
        fd.append("file", compressed);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        done += 1;
        setUploadProgress({ done, total: files.length });
        return data.path;
      };
      const queue = [...files];
      const results = new Array(files.length);
      const workers = Array.from({ length: Math.min(CONCURRENCY, files.length) }, async () => {
        while (queue.length) {
          const idx = files.length - queue.length;
          const file = queue.shift();
          results[idx] = await uploadOne(file);
        }
      });
      await Promise.all(workers);
      const uploaded = results.filter(Boolean);
      const nextPhotos = [...(item.photos || []), ...uploaded];
      const { data: updated } = await api.put(`/inventory/${id}`, { photos: nextPhotos });
      setItem(updated);
      toast.success(`${files.length} photo${files.length === 1 ? "" : "s"} uploaded.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "upload failed — please try again.");
    } finally {
      setUploading(false);
      setUploadProgress({ done: 0, total: 0 });
    }
  };

  const removePhoto = async (path) => {
    const next = (item.photos || []).filter((x) => x !== path);
    // Also clean up the channel map so the storefront filter doesn't carry stale keys.
    const nextChannels = { ...(item.photo_channels || {}) };
    delete nextChannels[path];
    try {
      const { data } = await api.put(`/inventory/${id}`, { photos: next, photo_channels: nextChannels });
      setItem(data);
    } catch (e) {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.error("photo remove failed", e);
      }
      toast.error("could not remove photo.");
    }
  };

  const reorderPhotos = async (nextPaths) => {
    // Optimistic UI — snap the new order immediately, then persist.
    const prevPhotos = item.photos || [];
    setItem({ ...item, photos: nextPaths });
    try {
      const { data } = await api.put(`/inventory/${id}`, { photos: nextPaths });
      setItem(data);
    } catch (e) {
      setItem({ ...item, photos: prevPhotos });
      toast.error("could not save photo order.");
    }
  };

  const toggleChannel = async (path, channel) => {
    const current = item.photo_channels || {};
    // Resolve the effective list (unmapped = all enabled).
    const enabled = current[path] !== undefined ? current[path] : ["shop", "ig", "ebay"];
    const next = enabled.includes(channel) ? enabled.filter((c) => c !== channel) : [...enabled, channel];
    const nextMap = { ...current, [path]: next };
    const prev = item;
    setItem({ ...item, photo_channels: nextMap });
    try {
      const { data } = await api.put(`/inventory/${id}`, { photo_channels: nextMap });
      setItem(data);
    } catch (e) {
      setItem(prev);
      toast.error("could not save channel.");
    }
  };

  const rotatePhoto = async (path) => {
    setRotatingPhoto(path);
    try {
      const { data } = await api.post(`/inventory/${id}/photos/rotate`, { path, direction: "cw" });
      if (data?.item) {
        setItem(data.item);
      }
    } catch (e) {
      toast.error("could not rotate photo.");
    } finally {
      setRotatingPhoto(null);
    }
  };

  const enhance = async (path) => {
    if (studio.mode === "custom" && !studio.custom_background_path) {
      toast.error("choose a custom backdrop first.");
      return;
    }
    if (studio.mode === "scene" && !studio.scene) {
      toast.error("choose a scene first.");
      return;
    }
    // Ignore re-clicks on a tile that's already enhancing — but don't block a click
    // on a DIFFERENT tile. Multiple concurrent enhances are now supported and the
    // previews arrive into a queue.
    if (enhancingPhotos[path]) return;
    setEnhancingPhotos((prev) => ({ ...prev, [path]: true }));
    try {
      const { data } = await api.post(`/inventory/${id}/enhance`, {
        photo_path: path,
        mode: studio.mode,
        background_color: studio.background_color || "#F6F2EB",
        color_name: studio.color_name || "",
        custom_background_path: studio.custom_background_path,
        scene: studio.scene || null,
      });
      // Queue the preview — the modal shows the head. If the user is already
      // reviewing another candidate, this one stacks and appears next.
      setPreviewQueue((prev) => [...prev, { source: data.source_path || path, candidate: data.path, mode: data.mode }]);
    } catch (e) {
      toast.error("enhancement failed: " + (e.response?.data?.detail || "try again"));
    } finally {
      setEnhancingPhotos((prev) => {
        const next = { ...prev };
        delete next[path];
        return next;
      });
    }
  };

  // Editorial batch — one click generates 3 candidates (hero shadow · lifestyle scene
  // · studio ghost mannequin) in parallel. All three land in the preview queue so the
  // seller can accept the best.
  const enhanceBatch = async (path) => {
    if (enhancingPhotos[path]) return;
    setEnhancingPhotos((prev) => ({ ...prev, [path]: true }));
    try {
      const { data } = await api.post(`/inventory/${id}/enhance/batch`, {
        photo_path: path,
        scene: studio.mode === "scene" ? studio.scene : null,
      });
      const ok = (data.candidates || []).filter((c) => c.ok);
      const fail = (data.candidates || []).filter((c) => !c.ok);
      if (ok.length) {
        setPreviewQueue((prev) => [
          ...prev,
          ...ok.map((c) => ({ source: c.source_path || path, candidate: c.path, mode: `${c.variant} · ${c.mode}` })),
        ]);
        toast.success(`${ok.length} candidate${ok.length === 1 ? "" : "s"} ready · ${fail.length ? `${fail.length} failed` : "all 3 succeeded"}`);
      } else {
        toast.error("editorial batch failed — try individual enhance.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "editorial batch failed.");
    } finally {
      setEnhancingPhotos((prev) => {
        const next = { ...prev };
        delete next[path];
        return next;
      });
    }
  };

  const acceptPreview = async () => {
    if (!preview) return;
    try {
      await api.post(`/inventory/${id}/enhance/accept`, { path: preview.candidate });
      await load();
      toast.success(`studio enhanced · ${preview.mode}. photo added.`);
      // Pop the head — the modal will re-render on the next queued preview if any.
      setPreviewQueue((prev) => prev.slice(1));
    } catch (e) {
      toast.error("could not accept the preview.");
    }
  };

  const discardPreview = async () => {
    if (!preview) return;
    try {
      await api.post(`/inventory/${id}/enhance/discard`, { path: preview.candidate });
    } catch (e) {
      // best-effort cleanup; the file is already orphaned in storage, log for diagnostics
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn("enhance discard cleanup failed", e);
      }
    }
    toast.info("preview discarded.");
    setPreviewQueue((prev) => prev.slice(1));
  };

  const sync = async (channel) => {
    try {
      const { data } = await api.post(`/inventory/${id}/sync`, { channel });
      toast.success(data.message);
      load();
    } catch (e) {
      toast.error("sync failed.");
    }
  };

  if (!item) return <AdminLayout><p className="editorial-label">loading…</p></AdminLayout>;

  const fld = (k, label, type = "text", suffix) => (
    <FieldInput k={k} label={label} type={type} suffix={suffix} value={item[k]} onChange={update} />
  );

  // Bind numeric inputs directly to state. The previous {...fld(k)} spread was
  // accidentally spreading a React element (React.$$typeof, .props, .key ...)
  // instead of {value, onChange}, so the shipping fields were uncontrolled and
  // never saved. `numProps` returns exactly the value/onChange props the input
  // needs; the surrounding <input> keeps its type/step/min/placeholder/testid.
  const numProps = (k) => ({
    value: item[k] ?? "",
    onChange: (e) => {
      const v = e.target.value;
      // Empty string stays empty (user cleared the field); otherwise coerce to
      // number so it round-trips through the API as a float, not a string.
      update(k, v === "" ? 0 : Number(v));
    },
  });

  // Weight is stored in kg in the DB. The studio admin types it in their preferred system
  // (kg or lb) and we convert on the way in/out. Measurements are free-text, but the label
  // hints at the active length unit.
  const weightField = (
    <div>
      <label className="editorial-label">weight</label>
      <div className="flex items-center gap-2 border-b border-taupe py-2 mt-2">
        <input
          type="number"
          step="0.01"
          min="0"
          value={kgToInput(item.weight, unitSystem)}
          onChange={(e) => update("weight", inputToKg(e.target.value, unitSystem))}
          onFocus={(e) => e.target.select()}
          className="flex-1 bg-transparent focus:outline-none text-charcoal"
          data-testid="weight-input"
        />
        <span className="text-xs tracking-widest uppercase text-taupe" data-testid="weight-unit">{weightLabel(unitSystem)}</span>
      </div>
    </div>
  );

  return (
    <AdminLayout>
      {preview && (
        <div className="fixed inset-0 z-50 bg-charcoal/70 backdrop-blur-sm flex items-center justify-center px-4 py-8 overflow-auto" data-testid="enhance-preview-dialog" onClick={discardPreview}>
          <div className="bg-ivory max-w-6xl w-full border border-taupe/50" onClick={(e) => e.stopPropagation()}>
            <div className="border-b border-taupe/40 px-6 py-4 flex items-center justify-between">
              <div>
                <p className="editorial-label">studio · before / after {previewQueue.length > 1 && <span className="text-brass ml-2">· {previewQueue.length - 1} more waiting</span>}</p>
                <h2 className="font-serif text-2xl lowercase text-charcoal mt-1">review the enhancement · mode <span className="text-brass">{preview.mode}</span></h2>
              </div>
              <button onClick={discardPreview} aria-label="close" className="text-taupe hover:text-charcoal p-1">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="grid md:grid-cols-2 gap-px bg-taupe/30">
              <figure className="bg-linen flex flex-col items-center p-4">
                <p className="editorial-label mb-3">before · source</p>
                <img src={fileUrl(preview.source)} alt="source" className="max-h-[60vh] w-auto object-contain" />
              </figure>
              <figure className="bg-linen flex flex-col items-center p-4">
                <p className="editorial-label mb-3 text-brass">after · candidate</p>
                <img src={fileUrl(preview.candidate)} alt="candidate" className="max-h-[60vh] w-auto object-contain" style={preview.mode === "transparent" ? { backgroundImage: "linear-gradient(45deg, #E8DED2 25%, transparent 25%), linear-gradient(-45deg, #E8DED2 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #E8DED2 75%), linear-gradient(-45deg, transparent 75%, #E8DED2 75%)", backgroundSize: "20px 20px", backgroundPosition: "0 0, 0 10px, 10px -10px, -10px 0px" } : undefined} />
              </figure>
            </div>
            <div className="border-t border-taupe/40 px-6 py-4 flex items-center justify-end gap-3">
              <button onClick={discardPreview} data-testid="preview-discard" className="border border-taupe text-charcoal hover:border-red-700 hover:text-red-700 px-5 py-2.5 text-xs tracking-[0.25em] uppercase">
                discard · keep original
              </button>
              <button onClick={acceptPreview} data-testid="preview-accept" className="bg-brass hover:bg-espresso text-ivory px-5 py-2.5 text-xs tracking-[0.25em] uppercase flex items-center gap-2">
                <BadgeCheck className="w-3 h-3" /> accept · add to photos
              </button>
            </div>
          </div>
        </div>
      )}
      <div data-testid="admin-item-edit" className="max-w-6xl">
        <button onClick={() => nav("/admin/inventory")} className="editorial-label hover:text-brass mb-6">← back to inventory</button>
        <div className="flex items-end justify-between mb-10">
          <div>
            <span className="editorial-label">edit · piece</span>
            <h1 className="font-serif text-5xl lowercase text-charcoal mt-2">{item.title || "untitled"}</h1>
            <div className="mt-3">
              <SaveIndicator
                status={autoSave.status}
                lastSavedAt={autoSave.lastSavedAt}
                lastError={autoSave.lastError}
                flush={autoSave.flush}
                testId="inv-save-indicator"
              />
            </div>
          </div>
          <button onClick={save} disabled={saving} data-testid="save-btn" className="bg-brass hover:bg-espresso text-ivory px-8 py-3 text-xs tracking-[0.25em] uppercase">
            {saving ? "saving…" : "save piece"}
          </button>
        </div>

        {/* Photos */}
        <section className="bg-linen border border-taupe/50 p-8 mb-10" data-testid="photos-section">
          <div className="flex items-end justify-between mb-6">
            <h2 className="font-serif text-2xl lowercase text-charcoal">photographs</h2>
            <label className="bg-brass hover:bg-espresso text-ivory px-5 py-2 text-xs tracking-[0.25em] uppercase cursor-pointer flex items-center gap-2" data-testid="upload-photo-label">
              <Upload className="w-3.5 h-3.5" />
              {uploading
                ? (uploadProgress.total > 1 ? `uploading… ${uploadProgress.done}/${uploadProgress.total}` : "uploading…")
                : "upload"}
              <input type="file" accept="image/*,.heic,.heif" multiple className="hidden" disabled={uploading} onChange={(e) => uploadPhoto(Array.from(e.target.files || []))} />
            </label>
          </div>

          {/* Image studio palette */}
          <ImageStudioPicker value={studio} onChange={setStudio} />

          <PhotoBoard
            photos={item.photos || []}
            photoChannels={item.photo_channels || {}}
            photoRoles={item.photo_roles || {}}
            enhancingPhotos={enhancingPhotos}
            rotatingPhoto={rotatingPhoto}
            onReorder={reorderPhotos}
            onRemove={removePhoto}
            onEnhance={enhance}
            onEnhanceBatch={enhanceBatch}
            onRotate={rotatePhoto}
            onToggleChannel={toggleChannel}
          />

          {/* Instagram-ready preview — sandbox the cross-post before publishing */}
          <details className="mt-10 group" data-testid="ig-preview-details">
            <summary className="cursor-pointer list-none flex items-center justify-between border-t border-taupe/40 pt-6 pb-3">
              <div className="flex items-center gap-2">
                <Instagram className="w-4 h-4 text-brass" />
                <span className="editorial-label">instagram-ready preview</span>
              </div>
              <span className="text-[0.6rem] tracking-widest uppercase text-taupe group-open:rotate-180 transition-transform">▾</span>
            </summary>
            <p className="text-xs text-espresso my-3">simulates the carousel post + how this piece will sit in your feed grid. only photos toggled for instagram appear here.</p>
            <InstagramPreview
              photos={item.photos || []}
              photoChannels={item.photo_channels || {}}
              item={item}
            />
          </details>
        </section>

        {/* Lookbook generator */}
        <LookbookGenerator itemId={item.id} photos={item.photos || []} />


        {/* Specs */}
        <SpecsSection
          item={item}
          categories={categories}
          unitSystem={unitSystem}
          fld={fld}
          update={update}
          setItem={setItem}
          loadCategories={loadCategories}
          weightField={weightField}
        />

        {/* Financials */}
        <FinancialsSection
          item={item}
          fld={fld}
          showBreakdown={showBreakdown}
          setShowBreakdown={setShowBreakdown}
          computedPrice={computedPrice}
          update={update}
        />

        {/* Physical shipping (weight + dimensions used by eBay calculated rates) */}
        <section className="border border-taupe/50 bg-linen p-8 mb-10" data-testid="shipping-physical-section">
          <div className="mb-4">
            <h2 className="font-serif text-2xl lowercase text-charcoal">shipping · physical</h2>
            <p className="text-xs text-espresso mt-1">
              used by eBay&apos;s calculated shipping rates. leave blank to use defaults (1 lb · 12×9×2 in — a padded USPS Priority mailer for a folded garment).
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-x-6 gap-y-4">
            <label className="flex flex-col">
              <span className="editorial-label mb-1">weight (lb)</span>
              <input
                type="number" step="0.1" min="0"
                {...numProps("shipping_weight_lb")}
                placeholder="1.0"
                className="border border-taupe/40 px-3 py-2 bg-white"
                data-testid="ship-weight-lb"
              />
            </label>
            <label className="flex flex-col">
              <span className="editorial-label mb-1">length (in)</span>
              <input
                type="number" step="0.5" min="0"
                {...numProps("shipping_length_in")}
                placeholder="12"
                className="border border-taupe/40 px-3 py-2 bg-white"
                data-testid="ship-length-in"
              />
            </label>
            <label className="flex flex-col">
              <span className="editorial-label mb-1">width (in)</span>
              <input
                type="number" step="0.5" min="0"
                {...numProps("shipping_width_in")}
                placeholder="9"
                className="border border-taupe/40 px-3 py-2 bg-white"
                data-testid="ship-width-in"
              />
            </label>
            <label className="flex flex-col">
              <span className="editorial-label mb-1">height (in)</span>
              <input
                type="number" step="0.5" min="0"
                {...numProps("shipping_height_in")}
                placeholder="2"
                className="border border-taupe/40 px-3 py-2 bg-white"
                data-testid="ship-height-in"
              />
            </label>
          </div>
        </section>

        {/* Sale Record */}
        <section className={`border p-8 mb-10 ${item.sold ? "bg-ivory border-brass" : "bg-linen border-taupe/50"}`} data-testid="sale-record-section">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2 className="font-serif text-2xl lowercase text-charcoal">sale record</h2>
              <p className="text-xs text-espresso mt-1">{item.sold ? "this piece has found its next story." : "register the sale when the piece is acquired."}</p>
            </div>
            {!item.sold && (
              <button onClick={() => setSaleDialog(true)} data-testid="mark-sold-btn" className="bg-brass hover:bg-espresso text-ivory px-5 py-2.5 text-xs tracking-[0.25em] uppercase flex items-center gap-2">
                <BadgeCheck className="w-4 h-4" /> mark sold
              </button>
            )}
          </div>
          {item.sold ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="sale-details">
              <div><p className="editorial-label">sold price</p><p className="font-serif text-2xl text-brass mt-1">{formatMoney(item.sold_price || 0)}</p></div>
              <div><p className="editorial-label">channel</p><p className="text-sm text-charcoal mt-2 uppercase tracking-widest text-[0.7rem]">{item.sold_channel || "—"}</p></div>
              <div><p className="editorial-label">buyer</p><p className="text-sm text-charcoal mt-1">{item.buyer_name || "—"}<br/><span className="text-xs text-taupe">{item.buyer_email || ""}</span></p></div>
              <div><p className="editorial-label">sold on</p><p className="text-sm text-charcoal mt-1">{item.sold_at ? new Date(item.sold_at).toLocaleDateString() : "—"}</p></div>
              {item.sale_notes && <div className="md:col-span-4"><p className="editorial-label">notes</p><p className="text-sm text-espresso mt-2 leading-relaxed">{item.sale_notes}</p></div>}
            </div>
          ) : (
            <p className="text-xs text-taupe italic">moving the stage to “sale completion” will also auto-flip this piece to sold (at the listed price). use “mark sold” to record buyer details.</p>
          )}
        </section>

        {/* Pipeline */}
        <section className="bg-linen border border-taupe/50 p-8 mb-10">
          <h2 className="font-serif text-2xl lowercase text-charcoal mb-6">pipeline stage</h2>
          <select data-testid="stage-select" value={item.stage} onChange={(e) => update("stage", e.target.value)} className="bg-transparent border-b border-taupe py-2 focus:outline-none text-charcoal w-full md:w-1/2">
            {STAGES.map((s) => <option key={s} value={s}>{s.toLowerCase()}</option>)}
          </select>
        </section>

        {/* Storefront visibility */}
        <ShopVisibility item={item} update={update} />

        {/* Visitor waitlist matches for this piece's category */}
        <section className="my-10" data-testid="waitlist-section">
          <WaitlistPanel itemId={item.id} stage={item.stage} />
        </section>

        {/* Omni-Channel */}
        <section className="bg-linen border border-taupe/50 p-8" data-testid="sync-section">
          <h2 className="font-serif text-2xl lowercase text-charcoal mb-6">omni-channel sync</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <button onClick={() => sync("storefront")} data-testid="sync-storefront" className="border border-taupe hover:border-brass p-5 text-left transition-colors">
              <ShoppingBag className="w-5 h-5 text-brass mb-3" />
              <p className="editorial-label">storefront</p>
              <p className="text-sm text-charcoal mt-1">{item.published_storefront ? "published live" : "publish to native"}</p>
            </button>
            <div data-testid="sync-ebay-tile" className={`border ${item.ebay_listing_id ? "border-emerald-700/40 bg-emerald-50/30" : "border-taupe hover:border-brass"} p-5 transition-colors`}>
              <Globe2 className={`w-5 h-5 mb-3 ${item.ebay_listing_id ? "text-emerald-800" : "text-brass"}`} />
              <p className="editorial-label">ebay</p>
              {item.ebay_listing_id ? (
                <div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-700" />
                    <p className="text-sm text-charcoal">listed · #{item.ebay_listing_id}</p>
                  </div>
                  <div className="flex items-center gap-3 mt-2">
                    {item.ebay_listing_url && (
                      <a href={item.ebay_listing_url} target="_blank" rel="noreferrer" className="text-xs underline text-brass" data-testid="ebay-view-listing">
                        view on eBay ↗
                      </a>
                    )}
                    <button onClick={async () => { if (!window.confirm("end this ebay listing? the item stays on other channels.")) return; try { await api.post(`/inventory/${item.id}/ebay/end`); toast.success("ebay listing ended."); load(); } catch (e) { toast.error(e.response?.data?.detail || "could not end listing."); } }} className="text-xs underline text-red-700" data-testid="ebay-end-listing">
                      end listing
                    </button>
                  </div>
                </div>
              ) : (
                <button onClick={() => sync("ebay")} data-testid="sync-ebay" className="text-sm text-charcoal mt-1 underline">
                  cross-post
                </button>
              )}
            </div>
            {/* IG / Poshmark cards — hidden by default now that Nifty owns
                listing copy generation. Show only when the admin explicitly
                opts back into the in-app manual generators via settings. */}
            {!settings?.hide_ai_listing_tools && (
              <>
                <button
                  onClick={() => setIgPrepOpen(true)}
                  data-testid="ig-prep-open-btn"
                  className={`border p-5 text-left transition-colors ${item.posted_to_instagram ? "border-emerald-700/40 bg-emerald-50/30" : "border-taupe hover:border-brass"}`}
                >
                  <Instagram className={`w-5 h-5 mb-3 ${item.posted_to_instagram ? "text-emerald-800" : "text-brass"}`} />
                  <p className="editorial-label">instagram</p>
                  <p className={`text-sm mt-1 ${item.posted_to_instagram ? "text-emerald-800" : "text-charcoal"}`}>
                    {item.posted_to_instagram
                      ? `posted${item.instagram_posted_at ? " · " + new Date(item.instagram_posted_at).toLocaleDateString() : ""}`
                      : "prep caption + photos"}
                  </p>
                  <p className="text-[0.6rem] uppercase tracking-widest text-taupe mt-2 flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> ai copy-paste
                  </p>
                </button>
                <button
                  onClick={() => setPoshPrepOpen(true)}
                  data-testid="posh-prep-open-btn"
                  className={`border p-5 text-left transition-colors ${item.posted_to_poshmark ? "border-emerald-700/40 bg-emerald-50/30" : "border-taupe hover:border-brass"}`}
                >
                  <ShoppingBag className={`w-5 h-5 mb-3 ${item.posted_to_poshmark ? "text-emerald-800" : "text-brass"}`} />
                  <p className="editorial-label">poshmark</p>
                  <p className={`text-sm mt-1 ${item.posted_to_poshmark ? "text-emerald-800" : "text-charcoal"}`}>
                    {item.posted_to_poshmark
                      ? `posted${item.poshmark_posted_at ? " · " + new Date(item.poshmark_posted_at).toLocaleDateString() : ""}`
                      : "prep listing + photos"}
                  </p>
                  <p className="text-[0.6rem] uppercase tracking-widest text-taupe mt-2 flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> ai copy-paste
                  </p>
                </button>
              </>
            )}
          </div>
          {!settings?.hide_ai_listing_tools && (
            <p className="editorial-label mt-6">instagram + poshmark use a copy-paste flow — ai writes the listing, download photos, paste from your phone. no third-party api risk.</p>
          )}

          {/* Nifty-managed toggle — when on, Maison Pilet's own eBay bulk / scheduled
              cross-post will skip this piece so Nifty owns cross-listing to eBay,
              Poshmark, Mercari, Depop etc. Prevents duplicate listings. */}
          <div className="mt-6 pt-6 border-t border-taupe/30 flex flex-wrap items-start gap-4" data-testid="nifty-managed-row">
            <label className="flex items-start gap-3 cursor-pointer flex-1 min-w-0">
              <input
                type="checkbox"
                checked={!!item.managed_by_nifty}
                onChange={async (e) => {
                  const next = e.target.checked;
                  const prev = item.managed_by_nifty;
                  setItem({ ...item, managed_by_nifty: next });
                  try {
                    await api.put(`/inventory/${id}`, { managed_by_nifty: next });
                    toast.success(next ? "nifty is now managing this piece." : "back to maison pilet cross-post.");
                  } catch (err) {
                    setItem({ ...item, managed_by_nifty: prev });
                    toast.error(err.response?.data?.detail || "could not save toggle.");
                  }
                }}
                className="mt-1 h-4 w-4 accent-brass flex-shrink-0"
                data-testid="managed-by-nifty-toggle"
              />
              <div className="min-w-0">
                <p className="text-sm text-charcoal">
                  <span className="uppercase tracking-widest text-[0.65rem] text-brass mr-2">nifty-managed</span>
                  skip maison pilet&apos;s eBay cross-post — nifty handles this piece.
                </p>
                <p className="text-xs text-espresso mt-1">
                  turn on for pieces you exported to Nifty so we don&apos;t double-list them on eBay. Nifty will cross-post to eBay + poshmark + mercari + depop from its own dashboard.
                </p>
              </div>
            </label>
            {item.managed_by_nifty && (
              <span className="text-[0.6rem] uppercase tracking-widest text-brass border border-brass/50 px-2 py-1 flex-shrink-0" data-testid="managed-by-nifty-badge">
                ★ nifty owns cross-post
              </span>
            )}
          </div>
        </section>

        {saleDialog && (
          <SaleDialog
            itemId={item.id}
            defaultPrice={item.sale_price}
            onClose={() => setSaleDialog(false)}
            onSaved={() => { setSaleDialog(false); load(); }}
          />
        )}

        {igPrepOpen && (
          <InstagramPrepModal
            item={item}
            onClose={() => setIgPrepOpen(false)}
            onPosted={() => load()}
          />
        )}

        {poshPrepOpen && (
          <PoshmarkPrepModal
            item={item}
            onClose={() => setPoshPrepOpen(false)}
            onPosted={() => load()}
          />
        )}

        {/* Danger zone — discontinue / reactivate, kept at the very bottom so destructive
            actions sit far away from regular editing. */}
        <DangerZone item={item} onChanged={(updated) => setItem(updated)} />
      </div>
    </AdminLayout>
  );
}
